import { ConfirmModal, If } from 'aa_common/front-end/components'
import { ifElse, someTrue } from 'aa_common/front-end/helpers'
import { MASTER_PAGES } from 'common/constants'
import { getTrimmedData, parseDataForSubmit, transformObjectWithPath } from 'common/helpers'
import { getNs } from 'common/i18n-config'
import { FORM_MODES } from 'common/models'
import { PresenterPreFillDraftAssetResponse } from 'common/open-api'
import { FeatureFlagNames } from 'common/open-features'
import { useCheckFlag } from 'common/open-features/hooks'
import { HorizontalLine } from 'components/atoms'
import { BasicForm } from 'components/molecules'
import {
  addApiErrorKey,
  checkAcquiredAtBelongToAssetCodeAutoGenerate,
} from 'components/organisms/assets/AssetForm/helper'
import { tagListSlice } from 'containers/master/tags/store'
import { unitsSlice } from 'containers/master/units/store'
import { get, isEqual } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { fetchMonthlyClosingCurrent } from 'store/accounting/monthlyClosing/actions'
import { fetchAutoGenerateSettings } from 'store/settings/assetCode/actions'
import { selectAssetCodeAutoGenerateSettingListData } from 'store/settings/assetCode/selectors'
import { cleanupExcise, fetchExciseList } from 'store/settings/excise/actions'

import { ReasonBox } from '../components/ReasonBox'
import ErrorComponent from './ErrorComponent'
import { FormData } from './FormData'
import { useAssetCorrection } from './hooks/use-asset-correction'
import { useFormatInitialAsset } from './hooks/use-format-initial-asset'
import { StyledTooltip, TopMessage, Wrapper } from './styles'
import { getAssetSchema, resetGeneralValidationErrors } from './validations/asset-schema'

const { t, useNsReady } = getNs(['components/organisms/assets/asset-form', 'common', 'components/molecules/basic-form'])

type Props = {
  errors?: any[]
  loading?: boolean
  initialAssetData: any
  formMode?: FORM_MODES
  confirming?: boolean
  isTemporaryData?: boolean
  onSubmit: (values: any) => void
  onSaveTemp?: (key: string, data: any, extraKey?: string) => void
  onDelete?: (biid: string) => void
  showRequiredError?: boolean
  asset?: any
  isValidateAssetLoading?: boolean
  isDraftFA?: boolean
  isReadOnlyDraftFA?: boolean
  calculatedDraftFA?: PresenterPreFillDraftAssetResponse
  shouldDisableSubmit?: boolean
  btnSubmitText?: string
  onDeleteButtonClick?: () => void
  disableDelete?: boolean
  onAcquiredAtChange?: (acquiredAt: string) => void
}

const AssetForm: React.FC<Props> = ({
  errors,
  loading = false,
  initialAssetData,
  formMode = FORM_MODES.NEW,
  confirming = false,
  isValidateAssetLoading = false,
  isTemporaryData = false,
  onSubmit,
  onSaveTemp,
  onDelete,
  showRequiredError,
  asset,
  isDraftFA,
  isReadOnlyDraftFA,
  calculatedDraftFA,
  shouldDisableSubmit,
  btnSubmitText,
  onDeleteButtonClick,
  disableDelete,
  onAcquiredAtChange,
}) => {
  useNsReady()

  const dispatch = useDispatch()
  const { biid } = useParams<{ biid: string }>()
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

  const { isEnable: isD80AcquisitionCostValidationEnabled } = useCheckFlag(
    FeatureFlagNames.FaD80AcquisitionCostValidation
  )
  const validationSchema = useMemo(
    () => getAssetSchema(!!isD80AcquisitionCostValidationEnabled),
    [isD80AcquisitionCostValidationEnabled]
  )
  const assetCodeAutoGenerateSettingListData = useSelector(selectAssetCodeAutoGenerateSettingListData, isEqual)

  const formattedAssetData = useFormatInitialAsset(initialAssetData)
  const {
    correctionBlockedReason,
    isCorrectionBlocked,
    isCorrectionLinkDisabled,
    isShowNoticeForChange,
    isShowCorrectionNotice,
    isShowImportPartialMessage,
    isShowCorrectionLink,
    isShowSuspenseCorrectionBlock,
  } = useAssetCorrection({ biid, formMode, asset: formattedAssetData, isDraftFA })

  const currentSetting = useMemo(() => {
    const acquiredAt = get(asset, 'acquired_at')

    if (someTrue(!assetCodeAutoGenerateSettingListData, !acquiredAt)) return null
    return assetCodeAutoGenerateSettingListData.find(setting =>
      checkAcquiredAtBelongToAssetCodeAutoGenerate(setting, acquiredAt)
    )
  }, [assetCodeAutoGenerateSettingListData, asset])

  const handleSubmit = useCallback(
    (values: any) => {
      const parsedValues = parseDataForSubmit(getTrimmedData(values))
      onSubmit(parsedValues)
    },
    [onSubmit]
  )

  const handleCancelDelete = (): void => {
    setIsConfirmingDelete(false)
  }

  const handleDelete = (): void => {
    onDelete?.(get(formattedAssetData, 'biid'))
    setIsConfirmingDelete(false)
  }

  const renderTopMessage = (children: string | React.ReactNode) => <TopMessage>{children}</TopMessage>

  const handleReloadMasterOptions = (pageName: string) => {
    if (pageName === MASTER_PAGES.UNITS) {
      unitsSlice.setFilters({}, true)
    }
  }

  useEffect(() => {
    dispatch(fetchExciseList({ filters: { is_active: true } }))
    dispatch(fetchAutoGenerateSettings())
    dispatch(fetchMonthlyClosingCurrent())
    tagListSlice.setFilters({}, true)
    unitsSlice.setFilters({}, true)

    return function cleanup() {
      dispatch(cleanupExcise())
      resetGeneralValidationErrors()
    }
  }, [])

  const apiErrors = useMemo(() => addApiErrorKey(transformObjectWithPath(errors)), [errors])

  return (
    <>
      <If condition={!!correctionBlockedReason}>
        <ReasonBox
          message={correctionBlockedReason?.message}
          linkText={correctionBlockedReason?.linkText}
          href={correctionBlockedReason?.href}
          className="correction-blocked-reason"
        />
      </If>
      <Wrapper>
        <If condition={isShowNoticeForChange}>{renderTopMessage(t('msg_notice_for_change'))}</If>
        <If condition={isShowCorrectionNotice}>{renderTopMessage(t('correction_notice_msg'))}</If>
        <If condition={isShowImportPartialMessage}>{renderTopMessage(t('cannot_correct_due_to_import_partial'))}</If>

        <If condition={isShowCorrectionLink}>
          {renderTopMessage(
            <>
              {t('correction_text_paragraph_1')}
              <StyledTooltip content={ifElse(isCorrectionLinkDisabled, t('tooltip_for_accelerated_depreciation'), '')}>
                <If condition={!isCorrectionLinkDisabled}>
                  <Link to={`/assets/${biid}/correction`}>{t('actions.correction')}</Link>
                </If>

                <If condition={isCorrectionLinkDisabled}>
                  <span className="adjusted">{t('actions.correction')}</span>
                </If>
              </StyledTooltip>
              {t('correction_text_paragraph_2')}
            </>
          )}
        </If>

        <If condition={isShowSuspenseCorrectionBlock}>{renderTopMessage(t('fa_from_suspense_correction_block'))}</If>

        <BasicForm
          initialValues={formattedAssetData}
          onSubmit={handleSubmit}
          errors={apiErrors}
          loading={loading || isValidateAssetLoading}
          preventSubmitOnEnter
          validationSchema={validationSchema}
        >
          <ErrorComponent errors={errors} values={asset} currentSetting={currentSetting} />
          <If condition={formattedAssetData}>
            <FormData
              initialValues={formattedAssetData}
              formMode={formMode}
              isTemporaryData={isTemporaryData}
              onSaveTemp={onSaveTemp}
              onHandleReloadMasterOptions={handleReloadMasterOptions}
              showRequiredError={showRequiredError}
              onSubmit={handleSubmit}
              confirming={confirming}
              isDraftFA={isDraftFA}
              isReadOnlyDraftFA={isReadOnlyDraftFA}
              calculatedDraftFA={calculatedDraftFA}
              shouldDisableSubmit={someTrue(!!shouldDisableSubmit, isCorrectionBlocked)}
              btnSubmitText={btnSubmitText}
              onDeleteButtonClick={onDeleteButtonClick}
              disableDelete={disableDelete}
              onAcquiredAtChange={onAcquiredAtChange}
              disableAllFields={isCorrectionBlocked}
            />
          </If>
          <HorizontalLine style={{ width: '100%', backgroundColor: '#D4D8DD' }} />

          <div style={{ height: 30 }} />
        </BasicForm>
        <ConfirmModal
          message={t('messages.confirm_delete', { name: get(formattedAssetData, 'name') })}
          visible={isConfirmingDelete}
          onOK={handleDelete}
          onCancel={handleCancelDelete}
        />
      </Wrapper>
    </>
  )
}

export default AssetForm
